/**
 * Canonicalização e validação de áudio OGG/Opus no cliente.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v1.0.0 - Versão inicial. Corrige o cabeçalho `OpusHead`
 *            gerado pelo gravador do navegador (pre-skip = 0) para o valor
 *            canônico usado pela libopus, reajusta as granule positions e
 *            recalcula o CRC das páginas alteradas. Objetivo: enviar nota de
 *            voz (PTT) para a Meta WhatsApp Cloud API — que só renderiza
 *            waveform/ícone de microfone com `.ogg` + codec Opus + voice=true —
 *            sem recodificar o áudio e sem transcodificação server-side.
 *
 * Importante: NÃO recodificamos o áudio. Os pacotes Opus originais são
 * preservados byte a byte; apenas metadados de container são normalizados.
 */

import { createLogger } from '@/lib/logger';

const oggLogger = createLogger('OggOpus');

/** Pre-skip canônico produzido pela libopus a 48 kHz (samples). */
export const CANONICAL_PRE_SKIP = 312;

/** Cabeçalho de página Ogg tem 27 bytes fixos + tabela de lacing. */
const OGG_HEADER_BYTES = 27;

/** Flag de última página (End Of Stream) no byte de header_type. */
const FLAG_EOS = 0x04;

/** Resultado da análise estrutural de um arquivo OGG/Opus. */
export interface OggOpusInfo {
  // Quantidade de páginas Ogg encontradas
  pageCount: number;
  // Canais declarados no OpusHead
  channels: number;
  // Pre-skip declarado no OpusHead
  preSkip: number;
  // Sample rate original declarado no OpusHead
  inputSampleRate: number;
  // Indica presença da página final (EOS)
  hasEos: boolean;
  // Última granule position (amostras em 48 kHz)
  lastGranule: number;
}

/** Erro de validação/canonicalização — mensagem já pronta para log. */
export class OggOpusError extends Error {}

/**
 * Tabela do CRC do Ogg (polinômio 0x04C11DB7, sem reflexão de bits).
 * Calculada uma única vez por sessão.
 */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let bit = 0; bit < 8; bit++) {
      r = r & 0x80000000 ? ((r << 1) ^ 0x04c11db7) >>> 0 : (r << 1) >>> 0;
    }
    table[i] = r >>> 0;
  }
  return table;
})();

/** Calcula o CRC de uma página Ogg (com o campo de CRC zerado). */
function computePageCrc(page: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < page.length; i++) {
    // Os 4 bytes de CRC (offsets 22..25) entram como zero no cálculo
    const byte = i >= 22 && i <= 25 ? 0 : page[i];
    crc = (((crc << 8) >>> 0) ^ CRC_TABLE[((crc >>> 24) & 0xff) ^ byte]) >>> 0;
  }
  return crc >>> 0;
}

/** Lê um inteiro de 32 bits little-endian. */
function readU32(bytes: Uint8Array, offset: number): number {
  return (
    (bytes[offset] |
      (bytes[offset + 1] << 8) |
      (bytes[offset + 2] << 16) |
      (bytes[offset + 3] << 24)) >>>
    0
  );
}

/** Escreve um inteiro de 32 bits little-endian. */
function writeU32(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = (value >>> 16) & 0xff;
  bytes[offset + 3] = (value >>> 24) & 0xff;
}

/** Lê a granule position (64 bits) como BigInt. */
function readGranule(bytes: Uint8Array, offset: number): bigint {
  let value = 0n;
  for (let i = 7; i >= 0; i--) value = (value << 8n) | BigInt(bytes[offset + i]);
  return value;
}

/** Escreve a granule position (64 bits) a partir de um BigInt. */
function writeGranule(bytes: Uint8Array, offset: number, value: bigint): void {
  let remaining = value;
  for (let i = 0; i < 8; i++) {
    bytes[offset + i] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }
}

/** Descritor interno de uma página Ogg localizada no buffer. */
interface OggPage {
  // Offset da página dentro do arquivo
  start: number;
  // Tamanho total da página (cabeçalho + lacing + payload)
  size: number;
  // Offset onde o payload começa
  payloadStart: number;
  // Flags do byte header_type
  flags: number;
}

/**
 * Percorre o arquivo e devolve todas as páginas Ogg.
 * Lança OggOpusError se a estrutura estiver corrompida ou incompleta.
 */
function parsePages(bytes: Uint8Array): OggPage[] {
  const pages: OggPage[] = [];
  let position = 0;

  while (position < bytes.length) {
    // Toda página precisa começar com a assinatura "OggS"
    if (position + OGG_HEADER_BYTES > bytes.length) {
      throw new OggOpusError('Página Ogg truncada no cabeçalho');
    }
    if (
      bytes[position] !== 0x4f ||
      bytes[position + 1] !== 0x67 ||
      bytes[position + 2] !== 0x67 ||
      bytes[position + 3] !== 0x53
    ) {
      throw new OggOpusError(`Assinatura OggS ausente no offset ${position}`);
    }

    const segmentCount = bytes[position + 26];
    const lacingStart = position + OGG_HEADER_BYTES;
    if (lacingStart + segmentCount > bytes.length) {
      throw new OggOpusError('Tabela de lacing truncada');
    }

    let payloadSize = 0;
    for (let i = 0; i < segmentCount; i++) payloadSize += bytes[lacingStart + i];

    const size = OGG_HEADER_BYTES + segmentCount + payloadSize;
    if (position + size > bytes.length) {
      throw new OggOpusError('Payload da página Ogg truncado');
    }

    pages.push({
      start: position,
      size,
      payloadStart: lacingStart + segmentCount,
      flags: bytes[position + 5],
    });

    position += size;
  }

  if (!pages.length) throw new OggOpusError('Nenhuma página Ogg encontrada');
  return pages;
}

/** Confere se o payload começa com uma assinatura textual (ex.: OpusHead). */
function payloadStartsWith(bytes: Uint8Array, page: OggPage, magic: string): boolean {
  for (let i = 0; i < magic.length; i++) {
    if (bytes[page.payloadStart + i] !== magic.charCodeAt(i)) return false;
  }
  return true;
}

/**
 * Valida integralmente um arquivo OGG/Opus (estrutura, codec e CRC).
 * Lança OggOpusError na primeira inconsistência encontrada.
 */
export function validateOggOpus(bytes: Uint8Array): OggOpusInfo {
  const pages = parsePages(bytes);

  // Página 0 deve conter o cabeçalho de identificação OpusHead
  const headPage = pages[0];
  if (!payloadStartsWith(bytes, headPage, 'OpusHead')) {
    throw new OggOpusError('Primeira página não contém OpusHead (não é Ogg/Opus)');
  }
  // Página 1 deve conter os comentários OpusTags
  if (pages.length < 2 || !payloadStartsWith(bytes, pages[1], 'OpusTags')) {
    throw new OggOpusError('Página de OpusTags ausente');
  }

  const headOffset = headPage.payloadStart;
  const channels = bytes[headOffset + 9];
  const preSkip = bytes[headOffset + 10] | (bytes[headOffset + 11] << 8);
  const inputSampleRate = readU32(bytes, headOffset + 12);

  if (channels !== 1) {
    // A Meta aceita apenas entrada mono para nota de voz
    throw new OggOpusError(`Áudio precisa ser mono para nota de voz (canais=${channels})`);
  }

  // Valida CRC, sequência e monotonia das granule positions
  let lastGranule = 0n;
  let hasEos = false;

  pages.forEach((page, index) => {
    const view = bytes.subarray(page.start, page.start + page.size);
    const storedCrc = readU32(bytes, page.start + 22);
    const computedCrc = computePageCrc(view);
    if (storedCrc !== computedCrc) {
      throw new OggOpusError(`CRC inválido na página ${index}`);
    }

    const sequence = readU32(bytes, page.start + 18);
    if (sequence !== index) {
      throw new OggOpusError(`Sequência de página fora de ordem (${sequence} != ${index})`);
    }

    const granule = readGranule(bytes, page.start + 6);
    if (index > 1) {
      if (granule < lastGranule) {
        throw new OggOpusError('Granule position não monotônica');
      }
      lastGranule = granule;
    }

    if (page.flags & FLAG_EOS) hasEos = true;
  });

  if (!hasEos) throw new OggOpusError('Stream sem página final (EOS)');
  if (lastGranule <= 0n) throw new OggOpusError('Stream sem áudio (granule final zerada)');

  return {
    pageCount: pages.length,
    channels,
    preSkip,
    inputSampleRate,
    hasEos,
    lastGranule: Number(lastGranule),
  };
}

/** Resultado da canonicalização. */
export interface CanonicalizedOggOpus {
  // Bytes do arquivo final (já validado)
  bytes: Uint8Array<ArrayBuffer>;
  // Pre-skip antes da correção
  originalPreSkip: number;
  // Pre-skip final
  finalPreSkip: number;
  // Quantidade de pacotes vazios removidos do fim do stream
  emptyPacketsRemoved: number;
  // Indica se algum byte precisou ser alterado
  patched: boolean;
  // Informações do arquivo final
  info: OggOpusInfo;
}

/**
 * Remove segmentos de lacing de tamanho zero no fim da última página.
 *
 * MOTIVO: o gravador do navegador encerra o stream com um pacote Opus vazio.
 * Decodificadores estritos falham nesse pacote ("Packet processing failed"),
 * e é o tipo de inconsistência que faz a Meta classificar o arquivo como
 * inválido no processamento assíncrono. Só removemos o zero final quando ele
 * realmente representa um pacote vazio (o segmento anterior é < 255).
 *
 * @returns nova página e quantidade de pacotes vazios removidos
 */
function stripTrailingEmptyPackets(
  page: Uint8Array<ArrayBuffer>,
): { page: Uint8Array<ArrayBuffer>; removed: number } {
  const segmentCount = page[26];
  const lacing = Array.from(page.subarray(OGG_HEADER_BYTES, OGG_HEADER_BYTES + segmentCount));
  const payload = page.subarray(OGG_HEADER_BYTES + segmentCount);

  let removed = 0;
  while (
    lacing.length > 1 &&
    lacing[lacing.length - 1] === 0 &&
    lacing[lacing.length - 2] !== 255
  ) {
    lacing.pop();
    removed++;
  }

  if (!removed) return { page, removed: 0 };

  // Reconstrói a página com a nova tabela de lacing (payload não muda:
  // segmentos removidos tinham tamanho zero)
  const rebuilt = new Uint8Array(new ArrayBuffer(OGG_HEADER_BYTES + lacing.length + payload.length));
  rebuilt.set(page.subarray(0, OGG_HEADER_BYTES), 0);
  rebuilt[26] = lacing.length;
  rebuilt.set(Uint8Array.from(lacing), OGG_HEADER_BYTES);
  rebuilt.set(payload, OGG_HEADER_BYTES + lacing.length);

  return { page: rebuilt, removed };
}

/**
 * Normaliza um OGG/Opus gravado no navegador para a forma canônica esperada
 * por decodificadores estritos (incluindo o processamento da Meta):
 *   - `OpusHead.pre-skip` mínimo canônico;
 *   - granule positions deslocadas de forma coerente com o novo pre-skip;
 *   - pacotes vazios no fim do stream removidos;
 *   - CRC recalculado em todas as páginas.
 */
export function canonicalizeOggOpus(input: Uint8Array): CanonicalizedOggOpus {
  // Validação de entrada antes de qualquer alteração (falha cedo e com clareza)
  const inputInfo = validateOggOpus(input);

  const sourcePages = parsePages(input);
  const originalPreSkip = inputInfo.preSkip;
  const needsPreSkipFix = originalPreSkip < CANONICAL_PRE_SKIP;
  const delta = needsPreSkipFix ? BigInt(CANONICAL_PRE_SKIP - originalPreSkip) : 0n;

  let emptyPacketsRemoved = 0;

  // Reconstrói página a página (tamanhos podem mudar ao remover lacing)
  const rebuiltPages = sourcePages.map((page, index) => {
    let current: Uint8Array<ArrayBuffer> = new Uint8Array(
      new ArrayBuffer(page.size),
    );
    current.set(input.subarray(page.start, page.start + page.size));

    // 1. Pre-skip canônico no OpusHead (primeira página)
    if (index === 0 && needsPreSkipFix) {
      const headOffset = OGG_HEADER_BYTES + current[26];
      current[headOffset + 10] = CANONICAL_PRE_SKIP & 0xff;
      current[headOffset + 11] = (CANONICAL_PRE_SKIP >> 8) & 0xff;
    }

    // 2. Granule positions coerentes com o novo pre-skip
    if (index >= 2 && needsPreSkipFix) {
      const granule = readGranule(current, 6);
      if (granule > 0n) writeGranule(current, 6, granule + delta);
    }

    // 3. Pacotes vazios no fim do stream (última página)
    if (index === sourcePages.length - 1) {
      const stripped = stripTrailingEmptyPackets(current);
      current = stripped.page;
      emptyPacketsRemoved += stripped.removed;
    }

    // 4. CRC recalculado sempre (barato e evita divergência silenciosa)
    writeU32(current, 22, 0);
    writeU32(current, 22, computePageCrc(current));

    return current;
  });

  const patched = needsPreSkipFix || emptyPacketsRemoved > 0;

  // Concatena as páginas em um único buffer
  const totalBytes = rebuiltPages.reduce((sum, page) => sum + page.length, 0);
  const bytes = new Uint8Array(new ArrayBuffer(totalBytes));
  let position = 0;
  for (const page of rebuiltPages) {
    bytes.set(page, position);
    position += page.length;
  }

  // 5. Revalida o resultado final — nada sai daqui sem passar de novo
  const info = validateOggOpus(bytes);

  oggLogger.debug('OGG/Opus canonicalizado', {
    originalPreSkip,
    finalPreSkip: info.preSkip,
    emptyPacketsRemoved,
    patched,
    pages: info.pageCount,
    bytes: bytes.length,
  });

  return {
    bytes,
    originalPreSkip,
    finalPreSkip: info.preSkip,
    emptyPacketsRemoved,
    patched,
    info,
  };
}

